import { createContext, useContext, useEffect, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { tenantApi, NetworkStatus } from "@/lib/api/client";
import { getConfig } from "@/lib/config";

interface NetworkContextType {
  status: NetworkStatus["status"];
  internalIp: string | null;
  isLoading: boolean;
  refresh: () => void;
}

const NetworkContext = createContext<NetworkContextType>({
  status: "detecting",
  internalIp: null,
  isLoading: true,
  refresh: () => {},
});

// Sidecar base URL for network detection (e.g. https://tenant-api.nodus.se).
// The customer's router/DNS resolves this to the local sidecar on the internal network.
const SIDECAR_URL = getConfig().SIDECAR_BASE_URL;

export function NetworkProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["network-status"],
    queryFn: async () => {
      // Step 1: Try to reach the callback server for a signed IP token
      let signedIpToken: string | undefined;
      try {
        const response = await fetch(`${SIDECAR_URL}/signed-ip-info`, {
          method: "GET",
          mode: "cors",
        });

        if (response.ok) {
          const responseData = await response.json();
          signedIpToken = responseData.token;
        } else {
          console.warn(`IP signer returned ${response.status}, treating as external`);
        }
      } catch (error) {
        // Network error reaching speedtest = likely external
        console.warn("Speedtest server unreachable, treating as external:", error);
      }

      // Step 2: Identify network via backend API
      try {
        return await tenantApi.identifyNetwork(signedIpToken);
      } catch (error) {
        console.error("Failed to identify network via API:", error);
        return { data: { status: "external" as const, internal_ip: null, connected_network: null, tenant_network: null, instructions: [] } };
      }
    },
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    retry: false,
  });

  // Handle visibility change to re-detect network
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refetch();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refetch]);

  return (
    <NetworkContext.Provider value={{ status: data?.data.status || "detecting", internalIp: data?.data.internal_ip || null, isLoading, refresh: refetch }}>
      {children}
    </NetworkContext.Provider>
  );
}

export function useNetworkStatus() {
  return useContext(NetworkContext);
}
